import { useState } from "react";
import { Button, Card, CardBody, CitationChip, ConfidenceBadge, TextInput } from "@odingard/design-system";
import { EmptyState, LoadingState } from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { CaiError } from "../../components/CaiError";

export function CaiReview() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const canWrite = activeOrg?.permissions.includes("connected_intelligence:write") ?? false;
  const state = useAsync(() => api.reviewQueue(orgId), [orgId]);
  const [busy, setBusy] = useState<string | null>(null);
  const [corrections, setCorrections] = useState<Record<string, string>>({});

  async function resolve(itemId: string, action: "accept" | "correct" | "reject") {
    setBusy(itemId);
    try {
      await api.resolveReview(orgId, itemId, action, action === "correct" ? corrections[itemId] : undefined);
      await state.reload();
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>AI Review</h1>
          <p>Extractions with low confidence requiring human verification before they&apos;re trusted.</p>
        </div>
      </div>

      {!canWrite && (
        <div className="banner banner--warning">Your access is read only. Resolving items requires the connected_intelligence:write permission.</div>
      )}
      {state.loading && <LoadingState />}
      {state.error && <CaiError error={state.error} onRetry={state.reload} />}
      {state.data &&
        (state.data.items.length === 0 ? (
          <EmptyState icon="✅" title="Review queue is clear" description="No extractions are awaiting verification." />
        ) : (
          <div className="ods-stack">
            {state.data.items.map((item) => (
              <Card key={item.id}>
                <CardBody>
                  <div className="ods-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>{item.contractTitle}</span>
                      <div style={{ fontWeight: 700 }}>{item.fieldLabel}</div>
                      <div style={{ margin: "4px 0", fontSize: "var(--ods-text-lg)" }}>{item.extractedValue}</div>
                      {item.citationLocator && <CitationChip locator={item.citationLocator} />}
                      {item.citationSnippet && <div className="citation-src" style={{ marginTop: 8 }}>{item.citationSnippet}</div>}
                    </div>
                    <ConfidenceBadge value={item.confidence} />
                  </div>
                  {canWrite && (
                    <div style={{ marginTop: "var(--ods-space-4)" }}>
                      <div className="ods-row">
                        <TextInput
                          placeholder="Corrected value (optional)"
                          value={corrections[item.id] ?? ""}
                          onChange={(e) => setCorrections((c) => ({ ...c, [item.id]: e.target.value }))}
                          aria-label={`Correct ${item.fieldLabel}`}
                        />
                      </div>
                      <div className="ods-row" style={{ marginTop: 10 }}>
                        <Button size="sm" disabled={busy === item.id} onClick={() => resolve(item.id, "accept")}>
                          Accept
                        </Button>
                        <Button size="sm" variant="secondary" disabled={busy === item.id || !corrections[item.id]} onClick={() => resolve(item.id, "correct")}>
                          Save correction
                        </Button>
                        <Button size="sm" variant="danger" disabled={busy === item.id} onClick={() => resolve(item.id, "reject")}>
                          Reject
                        </Button>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>
        ))}
    </>
  );
}
